import { formatDurationLabel } from './scheduleFormUtils';
import { generateLiveSessionName } from './liveSessionNaming';
import { DEFAULT_QA_DURATION_MINUTES } from './sessionQaStorage';
import {
  getSessionDisplayLabel,
  LIVE_SESSION_CATEGORIES,
} from '../config/liveSessionTypes';
import { normalizeSessionCategory } from './sessionCategoryUtils';

export type SessionCategory = 'mandatory' | 'workshop_online' | 'workshop_onsite' | 'optional';
export type LiveSessionRecordMode = 'configured' | 'tracking';
export type LiveSessionSessionType = 'online' | 'offline' | 'bootcamp' | 'tracking';

export const SESSION_CATEGORY_LABELS: Record<SessionCategory, string> = {
  mandatory: LIVE_SESSION_CATEGORIES.mandatory.label,
  workshop_online: LIVE_SESSION_CATEGORIES.workshop_online.label,
  workshop_onsite: LIVE_SESSION_CATEGORIES.workshop_onsite.label,
  optional: LIVE_SESSION_CATEGORIES.optional.label,
};

export const SESSION_CATEGORY_DESCRIPTIONS: Record<SessionCategory, string> = {
  mandatory: LIVE_SESSION_CATEGORIES.mandatory.description,
  workshop_online: LIVE_SESSION_CATEGORIES.workshop_online.description,
  workshop_onsite: LIVE_SESSION_CATEGORIES.workshop_onsite.description,
  optional: LIVE_SESSION_CATEGORIES.optional.description,
};

export const SESSION_TYPE_LABELS: Record<LiveSessionSessionType, string> = {
  online: 'Online',
  offline: 'Onsite',
  bootcamp: 'Bootcamp',
  tracking: 'Tracking',
};

export function formatPlannedSessionTypeLabel(session: {
  category: SessionCategory;
  liveSessionType: LiveSessionSessionType;
}): string {
  return getSessionDisplayLabel(session.category, session.liveSessionType);
}

export const SESSION_TYPE_OPTIONS: Array<{
  value: LiveSessionSessionType;
  label: string;
  description: string;
}> = [
  {
    value: 'online',
    label: 'Online',
    description: 'Live class or online workshop over video.',
  },
  {
    value: 'offline',
    label: 'Onsite',
    description: 'In-person onsite workshop or classroom delivery.',
  },
  {
    value: 'bootcamp',
    label: 'Bootcamp',
    description: 'High-intensity sprint with checkpoints.',
  },
  {
    value: 'tracking',
    label: 'Tracking',
    description: 'Short support session for progress review (10–30 min).',
  },
];

export const SESSION_TYPE_ROADMAPS: Record<LiveSessionSessionType, string[]> = {
  online: [
    'Welcome + agenda',
    'Concept walkthrough',
    'Live practice',
    'Quick quiz checkpoint',
    'Doubt clearing',
  ],
  offline: [
    'Warm-up and context',
    'Instructor demo',
    'Hands-on workshop',
    'Peer review and feedback',
    'Wrap-up and notes',
  ],
  bootcamp: [
    'Bootcamp kickoff',
    'Sprint round 1',
    'Checkpoint review',
    'Sprint round 2',
    'Final recap and test',
  ],
  tracking: [
    'Weekly progress review',
    'Topic difficulty check',
    'Support action points',
    'Follow-up plan',
  ],
};

export interface PlannedSession {
  id: string;
  title: string;
  batchId: string;
  batchLabel: string;
  studentCount: number;
  startDate: string;
  startTime: string;
  durationMinutes: number;
  topicCount: number;
  category: SessionCategory;
  notifyBatch: boolean;
  notifyTrainer: boolean;
  autoEndTest: boolean;
  aiProctoring: boolean;
  savedAt: string;
  status: 'scheduled' | 'draft';
  liveSessionRecordMode: LiveSessionRecordMode;
  liveSessionType: LiveSessionSessionType;
  trainerName: string;
  trainerAvatarUrl?: string;
  subjectName?: string;
  replacementTrainerName?: string;
  replacementTrainerAvatarUrl?: string;
  replacementRequested?: boolean;
  qaDurationMinutes?: number;
  deliveryTrainerId?: string;
  practiceTestEnabled?: boolean;
}

const LIST_KEY = 'codelive-planned-sessions-v4';
const LEGACY_KEY = 'codelive-saved-session';

function migrateLegacySession(): PlannedSession[] {
  try {
    const raw = sessionStorage.getItem(LEGACY_KEY);
    if (!raw) return [];
    const legacy = JSON.parse(raw) as Omit<PlannedSession, 'id' | 'status'>;
    sessionStorage.removeItem(LEGACY_KEY);
    return [
      {
        ...legacy,
        id: `session-${Date.now()}`,
        status: 'scheduled',
        liveSessionRecordMode: legacy.liveSessionRecordMode ?? 'configured',
        liveSessionType: legacy.liveSessionType ?? 'online',
        trainerName: legacy.trainerName ?? '',
        subjectName: legacy.subjectName ?? legacy.title,
      },
    ];
  } catch {
    return [];
  }
}

export function loadPlannedSessions(): PlannedSession[] {
  try {
    const raw = sessionStorage.getItem(LIST_KEY);
    if (!raw) {
      const migrated = migrateLegacySession();
      const initial = migrated.length > 0 ? migrated : [];
      sessionStorage.setItem(LIST_KEY, JSON.stringify(initial));
      return initial;
    }
    const parsed = JSON.parse(raw) as Partial<PlannedSession>[];
    return parsed.map((s) => ({
      ...s,
      category: normalizeSessionCategory(s.category, s.liveSessionType ?? 'online'),
      notifyTrainer: s.notifyTrainer ?? true,
      liveSessionRecordMode: s.liveSessionRecordMode ?? 'configured',
      liveSessionType: s.liveSessionType ?? 'online',
      trainerName: s.trainerName ?? '',
      subjectName: s.subjectName ?? s.title ?? '',
      qaDurationMinutes: s.qaDurationMinutes ?? DEFAULT_QA_DURATION_MINUTES,
    })) as PlannedSession[];
  } catch {
    return [];
  }
}

export function savePlannedSessions(sessions: PlannedSession[]): void {
  sessionStorage.setItem(LIST_KEY, JSON.stringify(sessions));
}

export function getPlannedSessionById(id: string): PlannedSession | null {
  return loadPlannedSessions().find((s) => s.id === id) ?? null;
}

export function updatePlannedSession(
  id: string,
  session: Omit<PlannedSession, 'id' | 'savedAt' | 'status'>,
): PlannedSession | null {
  const sessions = loadPlannedSessions();
  const index = sessions.findIndex((s) => s.id === id);
  if (index < 0) return null;
  const updated: PlannedSession = {
    ...sessions[index],
    ...session,
    id,
    savedAt: new Date().toISOString(),
  };
  sessions[index] = updated;
  savePlannedSessions(sessions);
  return updated;
}

export function cancelPlannedSession(id: string): void {
  savePlannedSessions(loadPlannedSessions().filter((s) => s.id !== id));
}

export function addPlannedSession(
  session: Omit<PlannedSession, 'id' | 'savedAt' | 'status'>,
): PlannedSession {
  const sessions = loadPlannedSessions();
  const sameBatchTitles = sessions
    .filter((s) => s.batchId && session.batchId && s.batchId === session.batchId)
    .map((s) => s.title);
  const title =
    session.title?.trim() ||
    generateLiveSessionName({
      category: session.category,
      subjectName: session.subjectName ?? '',
      startDate: session.startDate,
      existingNames: sameBatchTitles,
    });
  const newSession: PlannedSession = {
    ...session,
    title,
    id: `session-${Date.now()}`,
    savedAt: new Date().toISOString(),
    status: 'scheduled',
  };
  savePlannedSessions([newSession, ...sessions]);
  return newSession;
}

export function getSessionTypeRoadmap(type: LiveSessionSessionType): string[] {
  return SESSION_TYPE_ROADMAPS[type] ?? SESSION_TYPE_ROADMAPS.online;
}

export function getUpcomingConfiguredSession(): PlannedSession | null {
  return loadPlannedSessions()
    .filter((session) => session.liveSessionRecordMode === 'configured')
    .sort((a, b) => new Date(`${a.startDate}T${a.startTime}`).getTime() - new Date(`${b.startDate}T${b.startTime}`).getTime())[0] ?? null;
}

export type PlannedSessionBucket = 'ongoing' | 'upcoming' | 'completed';

export function getPlannedSessionBucket(
  session: PlannedSession,
  now = new Date(),
): PlannedSessionBucket {
  const start = new Date(`${session.startDate}T${session.startTime}:00`);
  const end = new Date(start.getTime() + session.durationMinutes * 60_000);
  if (Number.isNaN(start.getTime())) return 'upcoming';
  if (now < start) return 'upcoming';
  if (now <= end) return 'ongoing';
  return 'completed';
}

export function filterPlannedSessionsByBucket(
  sessions: PlannedSession[],
  bucket: PlannedSessionBucket,
  now = new Date(),
): PlannedSession[] {
  return sessions.filter((session) => getPlannedSessionBucket(session, now) === bucket);
}

export function countPlannedSessionsByBucket(
  sessions: PlannedSession[],
  now = new Date(),
): Record<PlannedSessionBucket, number> {
  return sessions.reduce(
    (counts, session) => {
      const bucket = getPlannedSessionBucket(session, now);
      counts[bucket] += 1;
      return counts;
    },
    { ongoing: 0, upcoming: 0, completed: 0 },
  );
}

export function formatSessionDateTime(date: string, time: string): string {
  const [y, m, d] = date.split('-');
  const [h, min] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${d}-${m}-${y} · ${h12}:${min} ${ampm}`;
}

export function formatSessionDuration(minutes: number): string {
  return formatDurationLabel(minutes);
}

/** @deprecated Use addPlannedSession */
export function saveScheduledSession(
  session: Omit<PlannedSession, 'id' | 'savedAt' | 'status'>,
): void {
  addPlannedSession(session);
}

export function loadScheduledSession(): PlannedSession | null {
  const sessions = loadPlannedSessions();
  return sessions[0] ?? null;
}
